import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Turnstile from 'react-turnstile';
import { StatusContext } from '../context/Status';
import {
  API,
  getCachedStatus,
  getRegistrationAvailability,
  showError,
  showInfo,
  showSuccess,
} from '../helpers';
import AuthLayout from '../layout/AuthLayout';

const RegisterForm = () => {
  const { t } = useTranslation();
  const [inputs, setInputs] = useState({
    username: '',
    password: '',
    password2: '',
    email: '',
    verification_code: '',
  });
  const [turnstileToken, setTurnstileToken] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationDisabled, setVerificationDisabled] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [statusState] = useContext(StatusContext);
  const navigate = useNavigate();
  const status = useMemo(
    () => statusState?.status || getCachedStatus(),
    [statusState?.status]
  );
  const registration = getRegistrationAvailability(status);
  const showEmailVerification = Boolean(status.email_verification);
  const turnstileEnabled = Boolean(status.turnstile_check);
  const turnstileSiteKey = status.turnstile_site_key || '';
  const { username, password, password2 } = inputs;

  useEffect(() => {
    const affCode = new URLSearchParams(window.location.search).get('aff');
    if (affCode) localStorage.setItem('aff', affCode);
  }, []);

  useEffect(() => {
    if (!verificationDisabled) return undefined;
    if (countdown <= 0) {
      setVerificationDisabled(false);
      setCountdown(30);
      return undefined;
    }
    const timer = setTimeout(
      () => setCountdown((current) => current - 1),
      1000
    );
    return () => clearTimeout(timer);
  }, [verificationDisabled, countdown]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setInputs((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    if (!registration.enabled) {
      showInfo(t(`auth.register.disabled_${registration.reason}`));
      return;
    }
    if (registerLoading) return;
    if (password.length < 8) {
      showInfo(t('messages.error.password_length'));
      return;
    }
    if (password !== password2) {
      showInfo(t('messages.error.password_mismatch'));
      return;
    }
    if (!username || !password) return;
    if (turnstileEnabled && !turnstileToken) {
      showInfo(t('messages.error.turnstile_wait'));
      return;
    }

    setRegisterLoading(true);
    try {
      const affCode =
        new URLSearchParams(window.location.search).get('aff') ||
        localStorage.getItem('aff');
      const payload = { ...inputs, aff_code: affCode };
      const res = await API.post(
        `/api/user/register?turnstile=${turnstileToken}`,
        payload
      );
      if (!res?.data) return;
      const { success, message } = res.data;
      if (success) {
        navigate('/login');
        showSuccess(t('messages.success.register'));
      } else {
        showError(message);
      }
    } finally {
      setRegisterLoading(false);
    }
  };

  const sendVerificationCode = async () => {
    if (!registration.enabled) {
      showInfo(t(`auth.register.disabled_${registration.reason}`));
      return;
    }
    if (!inputs.email || verificationLoading || verificationDisabled) return;
    if (turnstileEnabled && !turnstileToken) {
      showInfo(t('messages.error.turnstile_wait'));
      return;
    }
    setVerificationDisabled(true);
    setVerificationLoading(true);
    try {
      const res = await API.get(
        `/api/verification?email=${encodeURIComponent(
          inputs.email
        )}&turnstile=${turnstileToken}`
      );
      if (!res?.data) {
        setVerificationDisabled(false);
        setCountdown(30);
        return;
      }
      const { success, message } = res.data;
      if (success) {
        showSuccess(t('messages.success.verification_code'));
      } else {
        showError(message);
        setVerificationDisabled(false);
        setCountdown(30);
      }
    } finally {
      setVerificationLoading(false);
    }
  };

  return (
    <AuthLayout
      title={t('auth.register.title')}
      description={t('auth.register.subtitle')}
    >
      {registration.enabled ? (
        <>
          <form className='fvv-auth-form' onSubmit={handleSubmit}>
            <label className='fvv-field'>
              <span>{t('auth.register.username')}</span>
              <input
                className='fvv-input'
                name='username'
                value={inputs.username}
                onChange={handleChange}
                autoComplete='username'
                placeholder={t('auth.register.username')}
              />
            </label>
            <label className='fvv-field'>
              <span>{t('auth.register.password')}</span>
              <input
                className='fvv-input'
                name='password'
                type='password'
                value={inputs.password}
                onChange={handleChange}
                autoComplete='new-password'
                placeholder={t('auth.register.password')}
              />
            </label>
            <label className='fvv-field'>
              <span>{t('auth.register.confirm_password')}</span>
              <input
                className='fvv-input'
                name='password2'
                type='password'
                value={inputs.password2}
                onChange={handleChange}
                autoComplete='new-password'
                placeholder={t('auth.register.confirm_password')}
              />
            </label>

            {showEmailVerification && (
              <>
                <label className='fvv-field'>
                  <span>{t('auth.register.email')}</span>
                  <div className='fvv-filterbar'>
                    <input
                      className='fvv-input'
                      name='email'
                      type='email'
                      value={inputs.email}
                      onChange={handleChange}
                      placeholder={t('auth.register.email')}
                    />
                    <button
                      type='button'
                      className='fvv-ghost-btn'
                      onClick={sendVerificationCode}
                      disabled={
                        verificationLoading ||
                        verificationDisabled ||
                        !inputs.email
                      }
                    >
                      {verificationDisabled
                        ? t('auth.register.get_code_retry', { countdown })
                        : t('auth.register.get_code')}
                    </button>
                  </div>
                </label>
                <label className='fvv-field'>
                  <span>{t('auth.register.verification_code')}</span>
                  <input
                    className='fvv-input'
                    name='verification_code'
                    value={inputs.verification_code}
                    onChange={handleChange}
                    placeholder={t('auth.register.verification_code')}
                  />
                </label>
              </>
            )}

            {turnstileEnabled && turnstileSiteKey && (
              <Turnstile
                sitekey={turnstileSiteKey}
                size='flexible'
                fixedSize
                refreshExpired='auto'
                onVerify={setTurnstileToken}
                onExpire={() => setTurnstileToken('')}
                onError={() => setTurnstileToken('')}
              />
            )}

            <button
              className='fvv-solid-btn fvv-solid-btn--block'
              type='submit'
              disabled={registerLoading || !username || !password || !password2}
            >
              {registerLoading ? '...' : t('auth.register.button')}
            </button>
          </form>

          <div className='fvv-auth-footer'>
            {t('auth.register.has_account')}{' '}
            <Link to='/login'>{t('auth.register.login')}</Link>
          </div>
        </>
      ) : (
        <div className='fvv-auth-footer' role='status'>
          <strong>{t('auth.register.unavailable_title')}</strong>
          <p>{t(`auth.register.disabled_${registration.reason}`)}</p>
          <Link to='/login'>{t('auth.register.login')}</Link>
        </div>
      )}
    </AuthLayout>
  );
};

export default RegisterForm;
